"use client"

import { useState } from "react"
import { MessageSquare, MapPin, Clock, Send, CheckCircle2 } from "lucide-react"
import { useTranslations } from "next-intl"
import { pushLeadIntent } from "@/lib/gtm-data-layer"
import { PUBLIC_CONTACT_EMAIL } from "@/lib/contact-public"

// Canonical Window.gtag declaration lives in types/global.d.ts
// (centralised to avoid declaration-merging conflicts with
// other components — see I66 P5 build-fix incident 2026-05-09).

type ContactFormStatus = "idle" | "submitting" | "success" | "error"

function captureUtmFromLocation(): Record<string, string> {
  if (typeof window === "undefined") return {}
  const params = new URLSearchParams(window.location.search)
  const utm: Record<string, string> = {}
  for (const key of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
  ]) {
    const value = params.get(key)
    if (value) utm[key] = value
  }
  return utm
}

export function ContactForm() {
  const t = useTranslations("contact.form")
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
    honeypot: "",
  })
  const [status, setStatus] = useState<ContactFormStatus>("idle")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (status === "submitting" || status === "success") return

    setStatus("submitting")

    try {
      const sessionMetadata = {
        pageUrl: window.location.href,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        utm: captureUtmFromLocation(),
        timestamp: new Date().toISOString(),
      }

      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formData: {
            name: formData.name,
            email: formData.email,
            company: formData.company,
            service: formData.service,
            message: formData.message,
            honeypot: formData.honeypot,
          },
          sessionMetadata,
        }),
      })

      const result = await response.json().catch(() => ({}))

      if (!response.ok) {
        setStatus("error")
        return
      }

      setStatus("success")

      const dedupeId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `lead_${Date.now()}`

      pushLeadIntent({ formType: "contact", eventId: dedupeId })

      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "lead_intent", {
          cta_text: "Send message",
          cta_location: "contact_form",
          destination: "/contact",
          service_track: formData.service || undefined,
          event_id: dedupeId,
        })
      }
    } catch {
      setStatus("error")
    }
  }

  return (
    <section className="py-16">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-6">
            {t("howCanWeHelp")}
          </h2>

          <p className="text-muted-foreground leading-relaxed mb-8">
            {t("description")}
          </p>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-accent-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">
                  {t("contactChannelTitle")}
                </h4>
                <p className="text-muted-foreground text-sm">
                  {t("contactChannelValue")}
                </p>
                <p className="text-muted-foreground text-sm mt-2">
                  {t("contactEmailOrPrefix")}{" "}
                  <a
                    href={`mailto:${PUBLIC_CONTACT_EMAIL}`}
                    className="text-accent-primary hover:underline underline-offset-2"
                  >
                    {PUBLIC_CONTACT_EMAIL}
                  </a>
                  .
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-accent-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">{t("location")}</h4>
                <p className="text-muted-foreground text-sm">
                  {t("locationValue")}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-accent-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">
                  {t("responseTime")}
                </h4>
                <p className="text-muted-foreground text-sm">
                  {t("responseTimeValue")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 md:p-8 rounded-2xl bg-card/30 border border-border/50">
          <form onSubmit={handleSubmit} className="space-y-6">
            <input
              type="text"
              name="website"
              value={formData.honeypot}
              onChange={(e) =>
                setFormData({ ...formData, honeypot: e.target.value })
              }
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  {t("name")}
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent-primary/50 focus:border-accent-primary transition-colors"
                  placeholder={t("namePlaceholder")}
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  {t("email")}
                </label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent-primary/50 focus:border-accent-primary transition-colors"
                  placeholder={t("emailPlaceholder")}
                  required
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="company"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  {t("company")}
                </label>
                <input
                  type="text"
                  id="company"
                  value={formData.company}
                  onChange={(e) =>
                    setFormData({ ...formData, company: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent-primary/50 focus:border-accent-primary transition-colors"
                  placeholder={t("companyPlaceholder")}
                />
              </div>
              <div>
                <label
                  htmlFor="service"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  {t("serviceInterest")}
                </label>
                <select
                  id="service"
                  value={formData.service}
                  onChange={(e) =>
                    setFormData({ ...formData, service: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-lg bg-background border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-accent-primary/50 focus:border-accent-primary transition-colors"
                >
                  <option value="">{t("selectService")}</option>
                  <option value="strategy">{t("strategyDirection")}</option>
                  <option value="operations">{t("operationsExcellence")}</option>
                  <option value="data">{t("dataGovernance")}</option>
                  <option value="marketing">{t("marketingOperations")}</option>
                  <option value="technology">{t("technologySolutions")}</option>
                  <option value="people">{t("peopleOrganization")}</option>
                  <option value="other">{t("otherNotSure")}</option>
                </select>
              </div>
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-sm font-medium text-foreground mb-2"
              >
                {t("projectDescription")}
              </label>
              <textarea
                id="message"
                rows={5}
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                className="w-full px-4 py-3 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent-primary/50 focus:border-accent-primary transition-colors resize-none"
                placeholder={t("projectPlaceholder")}
                required
              />
            </div>

            <button
              type="submit"
              disabled={status === "submitting" || status === "success"}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-accent-primary px-6 py-3 text-sm font-medium text-background transition-all hover:bg-accent-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === "success" ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  {t("submitSuccess")}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {status === "submitting" ? t("submitting") : t("sendMessage")}
                </>
              )}
            </button>

            {status === "success" && (
              <p
                role="status"
                aria-live="polite"
                className="text-sm text-accent-primary text-center"
              >
                {t("submitSuccess")}
              </p>
            )}

            {status === "error" && (
              <p
                role="alert"
                className="text-sm text-destructive text-center"
              >
                {t("submitError")}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  )
}
